<script lang="ts">
  import { goto } from "$app/navigation";
  import { page } from "$app/stores";

  import { createEventDispatcher, getContext, hasContext } from "svelte";
  import type { Writable } from "svelte/store";

  export let href: string | undefined = undefined;
  export let value: string | undefined = undefined;

  type TabsContext = {
    activeTab: Writable<string | undefined>;
    queryParam: Writable<string | undefined>;
  };
  const { activeTab, queryParam } = hasContext("tabs")
    ? getContext<TabsContext>("tabs")
    : { activeTab: undefined, queryParam: undefined };

  $: isActive = href
    ? $page.url.pathname === href || $page.url.pathname.startsWith(href + "/")
    : $activeTab === value;

  function computeHref(
    url: string,
    queryParam: string | undefined,
    value: string | undefined,
  ) {
    if (!queryParam || !value) return "#";
    const urlObj = new URL(url);
    urlObj.searchParams.set(queryParam, value);
    return urlObj.toString();
  }

  $: computedHref =
    href || computeHref($page.url.toString(), $queryParam, value);

  const dispatch = createEventDispatcher<{
    click: MouseEvent;
  }>();

  function handleClick(e: MouseEvent) {
    if (!href && $queryParam && value) {
      // Use a JS click handler to avoid scroll on navigation
      e.preventDefault();
      goto(computedHref, { noScroll: true });
    }
    dispatch("click", e);
  }
</script>

<a
  href={computedHref}
  class="outer"
  role="tab"
  aria-selected={isActive}
  on:click={handleClick}
>
  <div class="inner" class:active={isActive}>
    <slot />
  </div>
</a>

<style lang="postcss">
  .outer {
    @apply translate-y-[1px] transition-colors px-4 first:-ml-4;
  }

  .inner {
    @apply text-sm leading-[1.5] font-medium text-white/60 border-b border-transparent py-3 flex items-center gap-1.5;
  }

  .active {
    @apply text-white border-primary;
  }

  .outer:hover :not(.active) {
    @apply text-white border-white;
  }
</style>
